// R58-L-10 — Promote selected button rendered in the Tiles widget
// header. Disabled when the selection is empty (AC6). On click, hands
// the current selection ids off to the L-08 + L-09 flow:
//
//   1. `runMapperPromote` → POST /ai-findings/bulk-promote
//      (mapper-driven; hits become defects silently).
//   2. If the response has `needsManual.length > 0`, the L-09 modal
//      opens (state `manualRows` is the batch modal payload).
//
// The L-10 acceptance spec (still passing) pins the captured call on
// `window.__r58l10PromoteCalls`; `runMapperPromote` preserves that
// boundary log.

import { Button, Tooltip } from "@mui/material";
import { useMemo, useState } from "react";
import type {
  BulkPromoteAiFindingsResponse,
  GeometryOverride,
  ImageRef,
} from "@aeriosiq/types";
// ANA-R1-11 — "Promote selected" conforms to the agreed analysis-surface
// action-button style via the shared `compactButtonXsSx` preset (same scale as
// the findings-list row Promote/Dismiss and the filter widget's controls).
import { compactButtonXsSx } from "@aeriosiq/ui";
import { useSelectionStore } from "../state/selectionStore.js";
import { runMapperPromote } from "../promote/PromoteController.js";
import { usePromoteWithMapper } from "../promote/useBulkPromote.js";
import {
  PromoteBatchModal,
  type NeedsManualRow,
} from "../promote/PromoteBatchModal.js";
// ANA-R3-GEOM-05 — Apply = the existing selection → "Promote selected" flow.
// For each selected finding with a session override we add a PIXEL geometry
// override to the bulk-promote body (normalised → × image dims). The store +
// the image/finding sources below give us the dims per finding.
import { useTileGeometryOverrideStore } from "./tileGeometryOverrideStore.js";
import { useImagesQuery } from "../queries/useImagesQuery.js";
import { useEffectiveFindingSet } from "../useEffectiveFindingSet.js";

/**
 * Build the PIXEL `geometryOverrides` map for the bulk-promote body. For each
 * selected finding that has a NORMALISED session override, look up its source
 * image's pixel dims and multiply (x × width, y × height). Findings with no
 * override — or whose image dims are unknown — are omitted (the BE then uses
 * their stored `finding_path`, `geometry_origin = model`).
 */
export function buildGeometryOverrides(
  findingIds: string[],
  overrides: Record<string, { path: number[] }>,
  imageByFindingId: Map<string, ImageRef>,
): Record<string, GeometryOverride> {
  const out: Record<string, GeometryOverride> = {};
  for (const findingId of findingIds) {
    const ov = overrides[findingId];
    if (!ov) continue;
    const img = imageByFindingId.get(findingId);
    // ANA-R4-PROMOTE-01 — guard the image dims: a missing/zero/NaN dim turns the
    // multiply into NaN, which the BE rejects with 400 ("geometryOverrides.each
    // value in path must be a number"). Skip → the BE promotes from the stored
    // finding_path instead of failing the whole batch.
    if (
      !img ||
      !Number.isFinite(img.width) ||
      !Number.isFinite(img.height) ||
      img.width <= 0 ||
      img.height <= 0
    ) {
      continue;
    }
    // The override must be a non-empty, even-length, all-finite normalised path.
    if (
      ov.path.length < 4 ||
      ov.path.length % 2 !== 0 ||
      !ov.path.every((v) => Number.isFinite(v))
    ) {
      continue;
    }
    const pixelPath = ov.path.map((v, i) =>
      i % 2 === 0 ? v * img.width : v * img.height,
    );
    // Belt-and-braces: never emit a non-finite pixel coordinate on the wire.
    if (!pixelPath.every((v) => Number.isFinite(v))) continue;
    out[findingId] = { path: pixelPath };
  }
  return out;
}

export interface TilePromoteButtonProps {
  /** Inspection / data-acquisition id — forwarded for tenant scoping. */
  inspectionId: string;
}

export function TilePromoteButton({
  inspectionId,
}: TilePromoteButtonProps): React.ReactElement {
  const selectedIds = useSelectionStore((s) => s.selectedFindingIds);
  const resetSelection = useSelectionStore((s) => s.reset);
  const disabled = selectedIds.size === 0;

  const mapperMut = usePromoteWithMapper(inspectionId);
  const [manualRows, setManualRows] = useState<NeedsManualRow[]>([]);
  const [modalOpen, setModalOpen] = useState(false);

  // ANA-R3-GEOM-05 — session overrides + the sources to resolve each finding's
  // image dims (for the normalised → pixel conversion).
  const overrides = useTileGeometryOverrideStore((s) => s.overrides);
  const clearOverride = useTileGeometryOverrideStore((s) => s.clear);
  const imagesQuery = useImagesQuery(inspectionId);
  const findingSet = useEffectiveFindingSet(inspectionId, { pageSize: 500 });

  // findingId → its source ImageRef, so we can scale the normalised override to
  // pixels (the tile crop already proved the image exists for an edited tile).
  const imageByFindingId = useMemo(() => {
    const imageById = new Map<string, ImageRef>();
    for (const it of imagesQuery.data?.items ?? [])
      imageById.set(it.imageId, it);
    const map = new Map<string, ImageRef>();
    for (const f of findingSet.query.data?.items ?? []) {
      const img = imageById.get(f.imageId);
      if (img) map.set(f.id, img);
    }
    return map;
  }, [imagesQuery.data, findingSet.query.data]);

  const onClick = (): void => {
    const findingIds = Array.from(selectedIds);
    // Effective geometry: a per-finding PIXEL override for every selected
    // finding that was edited this session; un-edited findings are omitted so
    // the BE promotes them from their stored geometry (geometry_origin=model).
    const geometryOverrides = buildGeometryOverrides(
      findingIds,
      overrides,
      imageByFindingId,
    );
    // ANA-R2-07 — `runMapperPromote` (below) ALREADY emits the L-10
    // acceptance-spec boundary log (`__r58l10PromoteCalls`) via the same
    // `captureCall`, so the separate `PromoteController.promoteMany` call that
    // used to sit here logged the click a SECOND time — the R58-L-10 AC7 spec
    // asserts exactly one capture. The real mutation has always run once (in
    // `runMapperPromote`); dropping the redundant log-only call fixes the
    // long-standing double-capture without changing promote behaviour.
    void runMapperPromote({
      findingIds,
      inspectionId,
      ...(Object.keys(geometryOverrides).length > 0
        ? { geometryOverrides }
        : {}),
      mutateAsync: (vars) => mapperMut.mutateAsync(vars),
      openManualModal: (resp: BulkPromoteAiFindingsResponse) => {
        const rows: NeedsManualRow[] = resp.needsManual.map((item) => ({
          findingId: item.findingId,
          mlClass: item.mlClass,
          // The L-08 response doesn't carry `side` — the modal then
          // falls back to "require chord% on every row" per its
          // conservative branch (BR-032 AC6 covers SS/PS; LE/TE rows can
          // be reverted post-promote if the analyst typed chord they
          // didn't need).
          side: null,
        }));
        setManualRows(rows);
        setModalOpen(true);
      },
    }).then(
      (resp) => {
        // ANA-R3-GEOM-05 — on a successful promote, clear the session override
        // for each finding the BE turned into a defect (`promoted`); a finding
        // that fell into `needsManual` keeps its override so the analyst can
        // retry/finish via the modal. Findings without an override are no-ops.
        for (const item of resp?.promoted ?? []) {
          if (overrides[item.findingId]) clearOverride(item.findingId);
        }
        // Reset selection once the mapper-driven batch finished —
        // mirrors how single-promote behaves. If a modal opened the
        // selection is reset only AFTER the modal closes via Apply
        // (handled in onPromoted below).
        if (resp?.needsManual.length === 0) {
          resetSelection();
        }
      },
      () => {
        /* error toast surfaces via the mutation; selection stays so the
         *  analyst can retry. */
      },
    );
  };

  return (
    <>
      {/* ANA-R2-07 AC3 — one plain-sentence tooltip on the action. A <span>
          wrapper keeps the tooltip working while the button is disabled
          (promoting) and avoids ref issues from wrapping the Button directly. */}
      <Tooltip title="Turn the selected AI findings into defects.">
        {/* `display:contents` so the wrapper carries the tooltip's hover/focus
            listeners (needed once the button is disabled while promoting)
            without adding a layout box. An explicit, STATE-STABLE aria-label
            keeps the button's accessible name "Promote selected" in every
            state — the visible label still flips to "Promoting…", and the
            tooltip never overrides the name (which would otherwise break
            role-by-name lookups when the disabled tooltip applies its title). */}
        <span style={{ display: "contents" }}>
          <Button
            size="small"
            variant="contained"
            color="primary"
            sx={compactButtonXsSx}
            disabled={disabled || mapperMut.isPending}
            onClick={onClick}
            data-testid="tile-promote-button"
            aria-label="Promote selected"
          >
            {mapperMut.isPending ? "Promoting…" : "Promote selected"}
          </Button>
        </span>
      </Tooltip>
      <PromoteBatchModal
        open={modalOpen}
        rows={manualRows}
        inspectionId={inspectionId}
        onClose={() => {
          setModalOpen(false);
        }}
        onPromoted={() => {
          resetSelection();
          setModalOpen(false);
        }}
      />
    </>
  );
}
